import { useCallback, useEffect, useState } from 'react';
import { Title, useCreatePath, useNotify } from 'react-admin';
import { Link, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { dateTimeNoSeconds } from '../../shared/dateTime';
import { FeaturesLoading, useFeatureState } from '../featureFlags/FeatureGate';
import { AvizationStateChip } from './AvizationQueue';
import { MessageDraft } from './MessageDraft';
import {
  FEATURE_CLIENT_AVIZATIONS,
  MIN_REASON,
  PHONE_RESULTS,
  SKIP_REASONS,
  fetchAvization,
  markAvizationDone,
  repeatAvization,
  saveAvizationDraft,
  savePhoneResult,
  skipAvization,
  toErrorMessage,
  type AvizationDetail,
} from './clientCommunicationApi';

/** Wiersz „etykieta — wartość — Kopiuj”; brak wartości = „brak danych” bez przycisku. */
const CopyRow = ({ label, value, testId }: { label: string; value: string | null; testId?: string }) => {
  const notify = useNotify();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value ?? '');
      notify(`Skopiowano: ${label.toLowerCase()}`, { type: 'info' });
    } catch {
      notify('Nie udało się skopiować — zaznacz i skopiuj ręcznie', { type: 'warning' });
    }
  };
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 30%) 1fr auto', gap: 1, alignItems: 'center' }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
        {value ?? <i>brak danych</i>}
      </Typography>
      {value ? (
        <Button size="small" onClick={() => void copy()} data-testid={testId}>
          Kopiuj
        </Button>
      ) : (
        <span />
      )}
    </Box>
  );
};

/**
 * Awizacja — wykonanie (makieta p. Mariusza: ekran „Awizacja”; prace
 * 26.10.2026, pakiet P1; przełącznik `clientAvizations`, S-26.10-14):
 *
 * - telefon — rozmówca, numer i tekst do przekazania z „Kopiuj”, wynik
 *   rozmowy i notatka („brak kontaktu” zostawia zadanie do ponowienia);
 * - mail — szkic do skopiowania albo otwarcia w poczcie, „Wysłane ręcznie”;
 * - platforma i Excel — pola z „Kopiuj”, „Oznacz jako wprowadzone”;
 * - „Pomiń z powodem”, a po wykonanej — „Ponów awizację”.
 *
 * Wynik trafia do sekcji 7 okna zlecenia i historii. Nic nie jest wysyłane.
 */
export const AvizationShow = () => {
  const on = useFeatureState(FEATURE_CLIENT_AVIZATIONS);
  const { id } = useParams<{ id: string }>();
  const notify = useNotify();
  const createPath = useCreatePath();
  const [a, setA] = useState<AvizationDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState('');
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');
  const [mode, setMode] = useState<'none' | 'skip' | 'repeat'>('none');

  const load = useCallback(() => {
    if (on !== true || !id) return;
    fetchAvization(id)
      .then((d) => {
        setA(d);
        setError(null);
      })
      .catch((e) => setError(toErrorMessage(e)));
  }, [on, id]);
  useEffect(load, [load]);

  if (on === undefined) return <FeaturesLoading />;
  if (!on) return null;

  const run = async (action: () => Promise<AvizationDetail>, message: string) => {
    setBusy(true);
    try {
      const next = await action();
      setA(next);
      notify(message, { type: 'success' });
      setMode('none');
      setReason('');
      setNote('');
      setResult('');
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!a) return <FeaturesLoading />;

  const todo = a.state === 'todo' && a.canWrite;
  const noteOk = note.trim().length >= 3;
  const resultReady = result !== '' && (result !== 'other' || noteOk);

  return (
    <Box sx={{ p: 1 }} data-testid="aviz-show">
      <Title title={`Awizacja ${a.id}`} />
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1.5}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="h6">
                Awizacja {a.id} · {a.channelLabel}
              </Typography>
              <AvizationStateChip a={a} />
            </Box>
            <Box sx={{ display: 'grid', gap: 0.5, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 220px), 1fr))' }}>
              <Typography variant="body2">
                Zlecenie:{' '}
                <Link to={createPath({ resource: 'user-offers', type: 'show', id: a.userOfferId })} data-testid="aviz-order-link">
                  {a.orderNumber}
                </Link>
              </Typography>
              <Typography variant="body2">Klient: {a.clientName ?? '—'}</Typography>
              <Typography variant="body2">Punkt: {a.pointLabel}</Typography>
              <Typography variant="body2">Termin awizacji: {dateTimeNoSeconds(a.dueAt)}</Typography>
              <Typography variant="body2">
                Źródło: {a.originLabel}
                {a.originNote ? ` (${a.originNote})` : ''}
              </Typography>
              {a.attempts > 0 ? <Typography variant="body2">Próby telefonu: {a.attempts}</Typography> : null}
            </Box>
            {a.state !== 'todo' ? (
              <Alert severity={a.state === 'done' ? 'success' : 'info'} data-testid="aviz-closed">
                {a.stateLabel}
                {a.doneByName ? ` — ${a.doneByName}, ${dateTimeNoSeconds(a.doneAt)}` : ''}
                {a.lastResultLabel ? ` · wynik: ${a.lastResultLabel}` : ''}
                {a.note ? ` · ${a.note}` : ''}
                {a.skipReason ? ` · powód: ${a.skipReason}` : ''}
              </Alert>
            ) : null}

            {a.channel === 'phone' ? (
              <Box data-testid="aviz-phone">
                <Typography variant="subtitle2">Rozmowa</Typography>
                <CopyRow label="Rozmówca" value={a.contactName} />
                <CopyRow label="Numer" value={a.contactPhone} testId="aviz-copy-phone" />
                <CopyRow label="Do przekazania" value={a.phoneScript} testId="aviz-copy-script" />
                {todo ? (
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    <TextField
                      select
                      size="small"
                      label="Wynik rozmowy"
                      value={result}
                      disabled={busy}
                      onChange={(e) => setResult(e.target.value)}
                      slotProps={{ htmlInput: { 'data-testid': 'cs-awizacja-wynik' } }}
                    >
                      {PHONE_RESULTS.map((r) => (
                        <MenuItem key={r.id} value={r.id}>
                          {r.name}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      size="small"
                      label={result === 'other' ? 'Notatka' : 'Notatka (opcjonalnie)'}
                      value={note}
                      multiline
                      minRows={2}
                      disabled={busy}
                      onChange={(e) => setNote(e.target.value)}
                      slotProps={{ htmlInput: { maxLength: 2000, 'data-testid': 'cs-awizacja-notatka' } }}
                    />
                    <Box>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={busy || !resultReady}
                        data-testid="cs-awizacja-zapisz"
                        onClick={() =>
                          void run(
                            () => savePhoneResult(a.id, { result, ...(note.trim() ? { text: note } : {}) }),
                            result === 'no_contact' ? 'Zapisano próbę — awizacja czeka na ponowienie' : 'Zapisano wynik rozmowy',
                          )
                        }
                      >
                        Zapisz wynik rozmowy
                      </Button>
                    </Box>
                  </Stack>
                ) : null}
              </Box>
            ) : null}

            {a.channel === 'email' ? (
              <Box data-testid="aviz-email">
                <Typography variant="subtitle2" gutterBottom>
                  Wiadomość
                </Typography>
                {a.body !== null ? (
                  <MessageDraft
                    subject={a.subject ?? ''}
                    body={a.body}
                    recipient={a.contactEmail}
                    editable={todo}
                    busy={busy}
                    testIdPrefix="aviz-mail"
                    onSave={(v) =>
                      run(() => saveAvizationDraft(a.id, { subject: v.subject, body: v.body, recipient: v.recipient }), 'Zapisano poprawki')
                    }
                    onSent={() => run(() => markAvizationDone(a.id), 'Awizacja oznaczona jako wysłana ręcznie')}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Szkic powstaje przy pierwszym otwarciu przez CS.
                  </Typography>
                )}
              </Box>
            ) : null}

            {a.channel === 'platform' || a.channel === 'excel' ? (
              <Box data-testid="aviz-platform">
                <Typography variant="subtitle2">
                  Dane do wprowadzenia w {a.channel === 'platform' ? 'platformie klienta' : 'pliku Excel klienta'}
                </Typography>
                {a.fields.map((f, i) => (
                  <CopyRow key={f.label} label={f.label} value={f.value} testId={`aviz-copy-field-${i}`} />
                ))}
                {todo ? (
                  <Button
                    sx={{ mt: 1 }}
                    variant="contained"
                    size="small"
                    disabled={busy}
                    data-testid="aviz-platform-done"
                    onClick={() => void run(() => markAvizationDone(a.id), 'Oznaczono jako wprowadzone')}
                  >
                    Oznacz jako wprowadzone
                  </Button>
                ) : null}
              </Box>
            ) : null}

            {a.canWrite && (a.state === 'todo' || a.state === 'done') ? (
              <Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {a.state === 'todo' ? (
                    <Button size="small" disabled={busy} onClick={() => setMode('skip')} data-testid="aviz-skip">
                      Pomiń z powodem
                    </Button>
                  ) : (
                    <Button size="small" disabled={busy} onClick={() => setMode('repeat')} data-testid="aviz-repeat">
                      Ponów awizację
                    </Button>
                  )}
                </Box>
                {mode !== 'none' ? (
                  <Stack spacing={1} sx={{ mt: 1 }} data-testid="aviz-reason-form">
                    {mode === 'skip' ? (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {SKIP_REASONS.slice(0, 2).map((r) => (
                          <Button key={r} size="small" variant="outlined" onClick={() => setReason(r)}>
                            {r}
                          </Button>
                        ))}
                      </Box>
                    ) : null}
                    <TextField
                      size="small"
                      label={mode === 'skip' ? 'Powód pominięcia' : 'Powód ponownej awizacji'}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      helperText={`co najmniej ${MIN_REASON} znaków`}
                      slotProps={{ htmlInput: { maxLength: 500, 'data-testid': 'aviz-reason' } }}
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={busy || reason.trim().length < MIN_REASON}
                        data-testid="aviz-reason-confirm"
                        onClick={() =>
                          void run(
                            () => (mode === 'skip' ? skipAvization(a.id, reason) : repeatAvization(a.id, reason)),
                            mode === 'skip' ? 'Awizacja pominięta' : 'Założono ponowną awizację',
                          )
                        }
                      >
                        {mode === 'skip' ? 'Pomiń' : 'Ponów'}
                      </Button>
                      <Button size="small" onClick={() => setMode('none')}>
                        Wróć
                      </Button>
                    </Box>
                  </Stack>
                ) : null}
              </Box>
            ) : null}
            <Typography variant="caption" color="text.secondary">
              Wynik i notatka trafiają do sekcji 7 okna zlecenia i do historii; widzą je FTL i Trade. Z aplikacji nic nie
              jest wysyłane.
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};
